import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// @TODO Replace me with a container equivalent
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Table from '@zg-rentals/multibrand-ui/lib/Table';

import { Container } from '@zillow/zrs-comet';

import { INVOICE_PAYMENTS } from 'app/shared/constants/RouteConstants';
import { NOTIFICATION_TYPE } from 'app/client/workers/notificationModule';

import LoadingConstants from 'app/shared/constants/LoadingConstants';

import BillingActions from 'app/shared/actions/BillingActions';

import PermissionCheck from 'app/shared/components/PermissionCheck';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import BillingError from 'app/shared/components/Billing/BillingError';
import BillingPaymentMethods from 'app/shared/components/Billing/BillingPaymentMethods';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import Breadcrumb from 'app/shared/components/Breadcrumb';
import { withErrorBoundary } from 'app/shared/components/ErrorBoundary';
import {
    Input,
    FormField,
    Text,
    LabeledControl,
    Radio,
    Label,
    Spacer,
    Flex,
    FormHelp,
    Alert,
} from '@zillow/constellation';

import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

import { AppSelectors, RoutingSelectors, BillingSelectors } from 'app/shared/selectors';

import numberUtils from 'app/shared/utils/numberUtils';
import billingUtils from 'app/shared/utils/billingUtils';
import pathsManifest from 'app/shared/pathsManifest';

import { StyledBannerText, StyledInvoicePaymentContainer } from './StyledComponents';

const propTypes = {
    currentInvoice: PropTypes.object,
    currentInvoiceLoadingState: PropTypes.string,
    currentInvoiceErrorMessage: PropTypes.string,
};

const defaultProps = {
    currentInvoice: null,
    currentInvoiceLoadingState: LoadingConstants.INITIAL,
    currentInvoiceErrorMessage: null,
};

const paths = pathsManifest.getAllPaths();

// Stylesheets
import '../InvoicePage/style.scss';

const InvoicePage = ({
    match,
    dispatch,
    currentInvoice,
    currentInvoiceLoadingState,
    currentInvoiceErrorMessage,
}) => {
    const [customAmount, setCustomAmount] = React.useState('');
    const [useBalance, setUseBalance] = React.useState(true);
    const [customAmountErrorMessage, setCustomAmountErrorMessage] = React.useState(null);
    const [displaySuccess, setDisplaySuccess] = React.useState(false);
    const [displayFailure, setDisplayFailure] = React.useState(false);

    const handleWebsocketMessge = (message) => {
        let data;
        let payload;
        try {
            data = JSON.parse(message.data);
            payload = JSON.parse(data.payload);
        } catch (error) {
            return;
        }
        if (data.name !== NOTIFICATION_TYPE.BILLING_PORTAL_PAYMENT_COMPLETE) {
            return;
        }
        if (payload.invoiceId !== currentInvoice.invoiceId) {
            return;
        }
        if (!payload.isSuccess) {
            dispatch(BillingActions.updateInvoicePayment(payload));
            setDisplayFailure(true);
            return;
        }
        // Success
        dispatch(BillingActions.setDisplayPaymentSuccess(true));
        window.router.transitionTo(`${paths.billingPage}/${paths.billingDue}`);
    };

    React.useEffect(() => {
        window.rentalManagerApiWebSocket.addEventListener('message', handleWebsocketMessge);
        return () => {
            window.rentalManagerApiWebSocket.removeEventListener('message', handleWebsocketMessge);
        };
    }, [currentInvoice]);

    React.useEffect(() => {
        dispatch(BillingActions.getInvoice(match.params.invoiceId));
        return () => {
            dispatch(BillingActions.clearCurrentInvoice());
        };
    }, []);

    const handleSetUseBalance = (newValue) => {
        setUseBalance(newValue);
    };

    const handleSubmit = (selectedCreditCardId, autopayEnabled) => {
        const newErrorMessage = billingUtils.validateInvoicePayment(currentInvoice, selectedCreditCardId, useBalance,
            customAmount);
        if (newErrorMessage) {
            return Promise.reject(new Error(newErrorMessage));
        }
        dispatch(BillingActions.setIsPaymentInProgress(true));
        setDisplaySuccess(false);
        setDisplayFailure(false);
        if (autopayEnabled) {
            dispatch(BillingActions.setPaymentMethodLock(true));
            dispatch(BillingActions.saveDefaultPaymentMethod({
                accountNumber: currentInvoice.accountNumber,
                defaultPaymentMethodId: selectedCreditCardId,
                autopayEnabled,
            })).catch(() => {
                // Todo handle error
            });
        }
        return dispatch(BillingActions.payInvoice({
            invoiceNumber: currentInvoice.invoiceNumber,
            paymentMethodId: selectedCreditCardId,
            amount: useBalance ? currentInvoice.balance : customAmount,
            partialPayment: !useBalance,
        }));
    };

    const renderPaymentOptions = () => (
        <>
            <FormField>
                <Label required={true}>
                    PAYMENT AMOUNT:
                </Label>
                <LabeledControl
                    controlId="LabeledControl-full-balance"
                    label={(
                        <Label>
                            {`Full Balance of ${numberUtils.asCurrency(currentInvoice.balance, '$0,0.00')}`}
                        </Label>
                    )}
                    control={(
                        <Radio
                            name="LabeledControl-full-balance"
                            checked={useBalance}
                            onChange={() => handleSetUseBalance(true)}
                        />
                    )}
                />
                <Spacer marginLeft="sm">
                    <Flex display="flex">
                        <LabeledControl
                            controlId="LabeledControl-other-amount"
                            label={<Label>Other Amount</Label>}
                            control={(
                                <Radio
                                    name="LabeledControl-other-amount"
                                    checked={!useBalance}
                                    onChange={() => handleSetUseBalance(false)}
                                />
                            )}
                        />
                        <Spacer marginLeft="md">
                            <Input
                                onChange={(e) => {
                                    const value = e.target.value.trim();
                                    const newCustomAmountValue = value === '0' ? '' : value;
                                    setCustomAmount(newCustomAmountValue);
                                    handleSetUseBalance(newCustomAmountValue === '');
                                    if (newCustomAmountValue > currentInvoice.balance) {
                                        setCustomAmountErrorMessage('The amount cannot be greater than the balance.');
                                    } else {
                                        setCustomAmountErrorMessage(null);
                                    }
                                }}
                                onFocus={() => handleSetUseBalance(false)}
                                placeholder="$"
                                type="number"
                                value={customAmount}
                                error={customAmountErrorMessage !== null}
                            />
                            {customAmountErrorMessage && (
                                <FormHelp error={true}>{customAmountErrorMessage}</FormHelp>
                            )}
                        </Spacer>
                    </Flex>
                </Spacer>
            </FormField>
            <Spacer marginBottom="sm" />
            <BillingPaymentMethods
                accountNumber={currentInvoice.accountNumber}
                onSubmit={handleSubmit}
                submitDisabled={(!useBalance && !customAmount)
                    || customAmountErrorMessage !== null || currentInvoice.isPaymentInProgress}
                submitLabel="Pay"
                isPaymentInProgress={currentInvoice.isPaymentInProgress}
                isSinglePayment={true}
                autoPayDisabled={!useBalance}
                infoMessage={!useBalance && !customAmount && 'Please enter an amount.'}
            />
        </>
    );

    const renderPaymentActions = () => {
        if (currentInvoice.balance > 0 && !currentInvoice.accountAutopay) {
            return renderPaymentOptions();
        }
        if (currentInvoice.accountAutopay) {
            return (
                <StyledBannerText as="h2">
                    Please turn off autopay before making a payment.
                </StyledBannerText>
            );
        }
        return (
            <StyledBannerText as="h2">
                No Payment Necessary.
            </StyledBannerText>
        );
    };

    const renderPaymentSection = () => (
        <Col xs={12} md={6}>
            {displaySuccess && (
                <>
                    <Alert
                        appearance="success"
                        body="Thank you for your payment!"
                    />
                    <Spacer marginBottom="sm" />
                </>
            )}
            {displayFailure && (
                <>
                    <Alert
                        appearance="error"
                        body="Payment failed!"
                    />
                    <Spacer marginBottom="sm" />
                </>
            )}
            {renderPaymentActions()}
        </Col>
    );

    const renderInvoiceDetails = () => (
        <Col xs={12} md={6}>
            <Table className="InvoicePage-details-table">
                <Table.Row>
                    <Table.Col>ACCOUNT</Table.Col>
                    <Table.Col>{currentInvoice.accountName}</Table.Col>
                </Table.Row>
                <Table.Row>
                    <Table.Col>INVOICE AMOUNT</Table.Col>
                    <Table.Col>{numberUtils.asCurrency(currentInvoice.amount, '$0,0.00')}</Table.Col>
                </Table.Row>
                <Table.Row>
                    <Table.Col>INVOICE BALANCE</Table.Col>
                    <Table.Col>{numberUtils.asCurrency(currentInvoice.balance, '$0,0.00')}</Table.Col>
                </Table.Row>
                <Table.Row>
                    <Table.Col>SERVICE PERIOD</Table.Col>
                    <Table.Col>{billingUtils.calculateServicePeriod(currentInvoice.invoiceItems)}</Table.Col>
                </Table.Row>
                <Table.Row>
                    <Table.Col>AUTOMATIC PAYMENTS</Table.Col>
                    <Table.Col>{currentInvoice.accountAutopay ? 'YES' : 'NO'}</Table.Col>
                </Table.Row>
            </Table>
        </Col>
    );

    const isLoaded = currentInvoiceLoadingState === LoadingConstants.SUCCESS;

    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <PermissionCheck route={INVOICE_PAYMENTS}>
                <div className="InvoicePage">
                    <HeadWrapper title="Make a payment" />
                    <Container>
                        <Breadcrumb
                            title="Back"
                            redirectTo={`/rental-manager/billing/invoice/${match.params.invoiceId}`}
                        />
                        <StyledInvoicePaymentContainer borderFull={true} borderRounded={true}>
                            <Spacer padding="sm">
                                <Container style={{ padding: 0 }}>
                                    <Row>
                                        <Col>
                                            <Text fontType="h3" as="h3">
                                                Make a payment
                                            </Text>
                                        </Col>
                                    </Row>
                                    <Spacer paddingBottom="lg" />
                                    <Row>
                                        <Col>
                                            <Text fontType="h4" as="h4">
                                                {`#${match.params.invoiceId}`}
                                            </Text>
                                        </Col>
                                    </Row>
                                    {!isLoaded && currentInvoiceLoadingState !== LoadingConstants.ERROR && (
                                        <Row>
                                            <SpinnerContainer />
                                        </Row>
                                    )}
                                    {isLoaded && (
                                        <Row noPadding={true}>
                                            {renderInvoiceDetails()}
                                            {renderPaymentSection()}
                                        </Row>
                                    )}
                                </Container>
                            </Spacer>
                        </StyledInvoicePaymentContainer>
                    </Container>
                    {currentInvoiceErrorMessage && (
                        <Container>
                            <BillingError errorMsg={currentInvoiceErrorMessage} />
                        </Container>
                    )}
                </div>
            </PermissionCheck>
        </AuthCheckWrapper>
    );
};

const mapStateToProps = state => ({
    router: RoutingSelectors.makeRouting(state),
    currentInvoice: state.billing.invoice.currentInvoice,
    currentInvoiceLoadingState: BillingSelectors.makeCurrentInvoiceLoadingState(state),
    currentInvoiceErrorMessage: BillingSelectors.makeCurrentInvoiceErrorMessage(state),
    cookieEnabledServices: AppSelectors.makeCookieEnabledServices(state),
});

InvoicePage.propTypes = propTypes;
InvoicePage.defaultProps = defaultProps;

export { InvoicePage };
export default withErrorBoundary(connect(mapStateToProps)(InvoicePage));
